/**
 * Financial Splitter Utility
 * Splits financial data between heat company (varmeselskab) and electricity company (elselskab)
 * Based on asset usage and revenue streams
 */

/**
 * Allocation rules for CAPEX between heat and electricity companies
 * Based on CEO's spreadsheet classification (Skaelsker Fjernvarme vs Skaelsker Energi)
 */
const CAPEX_ALLOCATION_RULES = {
  elkedel: { heat: 0.0, electricity: 1.0 },        // 100% Skaelsker Energi
  htes: { heat: 1.0, electricity: 0.0 },           // 100% Skaelsker Fjernvarme
  tilslutning: { heat: 0.0, electricity: 1.0 },    // 100% Skaelsker Energi (Elnet tilslutning)
  batterier: { heat: 0.0, electricity: 1.0 },      // 100% Skaelsker Energi
  ltes: { heat: 1.0, electricity: 0.0 },           // 100% Skaelsker Fjernvarme
  turbine: { heat: 1.0, electricity: 0.0 },        // 100% Skaelsker Fjernvarme
  ledningsnet: { heat: 1.0, electricity: 0.0 },    // 100% Skaelsker Fjernvarme
  bygninger: { heat: 1.0, electricity: 0.0 },      // 100% Skaelsker Fjernvarme
  orc: { heat: 1.0, electricity: 0.0 },            // 100% Skaelsker Fjernvarme
  projektledelse: { heat: 1.0, electricity: 0.0 }, // 100% Skaelsker Fjernvarme
  software: { heat: 1.0, electricity: 0.0 },       // 100% Skaelsker Fjernvarme
  diverse: { heat: 1.0, electricity: 0.0 },        // 100% Skaelsker Fjernvarme
  tilkoblinger: { heat: 1.0, electricity: 0.0 },   // 100% Skaelsker Fjernvarme
  industrigrund: { heat: 1.0, electricity: 0.0 },  // 100% Skaelsker Fjernvarme
  veksler: { heat: 1.0, electricity: 0.0 },        // 100% Skaelsker Fjernvarme
};

/**
 * Splits financial data between heat and electricity companies
 * @param {Object} stats - Simulation statistics from SimulationEngineV2
 * @param {Object} config - Simulation configuration
 * @returns {Object} FinancialSplit object with heat/electricity breakdown
 */
export const splitFinancialData = (stats, config) => {
  if (!stats || !config) {
    return getEmptyFinancialSplit();
  }

  // Calculate CAPEX split
  const capexSplit = allocateCapex(config);

  // Calculate OPEX split
  const opexSplit = allocateOpex(stats);

  // Calculate Depreciation split (needed for heat price calculation)
  const depreciationSplit = allocateDepreciation();

  // Calculate Revenue split (uses OPEX and Depreciation for dynamic heat price)
  const revenueSplit = allocateRevenue(stats, opexSplit, depreciationSplit);
  
  // Calculate profit/loss for each company
  // EBITDA = Revenue - OPEX
  const heatEbitda = revenueSplit.heat.total - opexSplit.heat.total;
  const electricityEbitda = revenueSplit.electricity.total - opexSplit.electricity.total;

  // Net Profit = EBITDA - Depreciation (convert depreciation from M DKK to DKK)
  const heatNetProfit = heatEbitda - (depreciationSplit.heat.total * 1000000);
  const electricityNetProfit = electricityEbitda - (depreciationSplit.electricity.total * 1000000);
  
  return {
    heat: {
      capex: capexSplit.heat.breakdown,
      opex: opexSplit.heat.breakdown,
      revenue: revenueSplit.heat.breakdown,
      depreciation: depreciationSplit.heat.breakdown,
      totalCapex: capexSplit.heat.total,
      totalOpex: opexSplit.heat.total,
      totalRevenue: revenueSplit.heat.total,
      totalDepreciation: depreciationSplit.heat.total,
      ebitda: heatEbitda,
      netProfit: heatNetProfit,
    },
    electricity: {
      capex: capexSplit.electricity.breakdown,
      opex: opexSplit.electricity.breakdown,
      revenue: revenueSplit.electricity.breakdown,
      depreciation: depreciationSplit.electricity.breakdown,
      totalCapex: capexSplit.electricity.total,
      totalOpex: opexSplit.electricity.total,
      totalRevenue: revenueSplit.electricity.total,
      totalDepreciation: depreciationSplit.electricity.total,
      ebitda: electricityEbitda,
      netProfit: electricityNetProfit,
    },
    combined: {
      totalCapex: capexSplit.heat.total + capexSplit.electricity.total,
      totalOpex: opexSplit.heat.total + opexSplit.electricity.total,
      totalRevenue: revenueSplit.heat.total + revenueSplit.electricity.total,
      totalDepreciation: depreciationSplit.heat.total + depreciationSplit.electricity.total,
      ebitda: heatEbitda + electricityEbitda,
      netProfit: heatNetProfit + electricityNetProfit,
    },
  };
};

/**
 * Allocates CAPEX between heat and electricity companies
 * @param {Object} config - Simulation configuration
 * @returns {Object} CAPEX allocation
 */
export const allocateCapex = (config) => {
  // CAPEX values (in million DKK) - exact values from CEO's spreadsheet
  const capexValues = {
    elkedel: 30.0,      // 30 MW - Skaelsker Energi
    htes: 40.0,         // 2x150 MWh Rock store - Skaelsker Fjernvarme
    tilslutning: 9.0,   // Elnet - Skaelsker Fjernvarme
    batterier: 9.0,     // 3 MW kapacitet - Skaelsker Energi
    ltes: 6.4,          // 3100 M3, 205 MWh - Skaelsker Fjernvarme
    turbine: 5.7,       // 10 MW - Skaelsker Fjernvarme
    ledningsnet: 286,   // Infrastruktur - Skaelsker Fjernvarme
    bygninger: 3.15,    // Bygninger - Skaelsker Fjernvarme
    orc: 0.6,           // ORC anlæg - Skaelsker Fjernvarme
    projektledelse: 6.0, // Projektledelse - Skaelsker Fjernvarme
    software: 1.5,      // Software - Skaelsker Fjernvarme
    diverse: 4.85,      // Diverse - Skaelsker Fjernvarme
    tilkoblinger: 29.5, // 1475 tilkoblinger - Skaelsker Fjernvarme
    industrigrund: 4.0, // Industrigrund - Skaelsker Fjernvarme
    veksler: 5.0,       // Veksler til overskudsvarme - Skaelsker Fjernvarme
  };

  const totalCapex = Object.values(capexValues).reduce((sum, val) => sum + val, 0); // 407.6 M DKK
  
  // Allocate to heat company (Skaelsker Fjernvarme)
  const heatCapex = {
    htes: capexValues.htes * CAPEX_ALLOCATION_RULES.htes.heat,
    ltes: capexValues.ltes * CAPEX_ALLOCATION_RULES.ltes.heat,
    turbine: capexValues.turbine * CAPEX_ALLOCATION_RULES.turbine.heat,
    ledningsnet: capexValues.ledningsnet * CAPEX_ALLOCATION_RULES.ledningsnet.heat,
    bygninger: capexValues.bygninger * CAPEX_ALLOCATION_RULES.bygninger.heat,
    orc: capexValues.orc * CAPEX_ALLOCATION_RULES.orc.heat,
    projektledelse: capexValues.projektledelse * CAPEX_ALLOCATION_RULES.projektledelse.heat,
    software: capexValues.software * CAPEX_ALLOCATION_RULES.software.heat,
    diverse: capexValues.diverse * CAPEX_ALLOCATION_RULES.diverse.heat,
    tilkoblinger: capexValues.tilkoblinger * CAPEX_ALLOCATION_RULES.tilkoblinger.heat,
    industrigrund: capexValues.industrigrund * CAPEX_ALLOCATION_RULES.industrigrund.heat,
    veksler: capexValues.veksler * CAPEX_ALLOCATION_RULES.veksler.heat,
  };

  // Allocate to electricity company (Skaelsker Energi)
  const electricityCapex = {
    elkedel: capexValues.elkedel * CAPEX_ALLOCATION_RULES.elkedel.electricity,
    tilslutning: capexValues.tilslutning * CAPEX_ALLOCATION_RULES.tilslutning.electricity,
    batterier: capexValues.batterier * CAPEX_ALLOCATION_RULES.batterier.electricity,
  };
  
  const heatTotal = Object.values(heatCapex).reduce((sum, val) => sum + val, 0); // 352.5 M DKK
  const electricityTotal = Object.values(electricityCapex).reduce((sum, val) => sum + val, 0); // 39.0 M DKK
  
  return {
    heat: { breakdown: heatCapex, total: heatTotal },
    electricity: { breakdown: electricityCapex, total: electricityTotal },
    combined: { total: totalCapex },
  };
};

/**
 * Allocates OPEX between heat and electricity companies
 * @param {Object} stats - Simulation statistics
 * @returns {Object} OPEX allocation
 */
export const allocateOpex = (stats) => {
  // Variable costs from CEO's spreadsheet (all in DKK)
  // Elkøb til operational heating (KUN Grid Spot + TSO, IKKE solar) - går til Fjernvarme
  const operationalElPurchases = (stats.operationalGridCost || 0);

  // Elkøb til arbitrage (kun Grid Spot, ingen TSO) - går til Energi
  const arbitrageElPurchases = (stats.arbitrageGridCost || 0) + (stats.arbitrageSolarCost || 0);

  // Sol til operational (Spotpris uden TSO) - går til Fjernvarme (SEPARAT linje)
  const operationalSolarPurchases = stats.operationalSolarCost || 0;
  
  // Overskudsvarme (150 DKK/MWh) - går til Fjernvarme
  const wasteHeatCost = (stats.wasteHeatAccepted || 0) * 150;

  // Variable costs based on MWh sold
  const netDelivery = stats.netDelivery || 0;
  const administrationCost = netDelivery * 50; // 50 DKK per MWh solgt
  const driftVedligeholdCost = netDelivery * 175; // 175 DKK per MWh solgt

  // Fixed costs (all to Skaelsker Fjernvarme per CEO's spreadsheet)
  const fixedCosts = {
    forsyningDkLicens: 3000000,  // 3.0 M DKK - Forsyning Danmark licens
    it: 750000,                   // 0.75 M DKK - IT omkostninger
    revision: 250000,             // 0.25 M DKK - Revision
    administration: administrationCost, // 50 DKK/MWh solgt
    driftVedligehold: driftVedligeholdCost, // 175 DKK/MWh solgt
    diverse: 2000133,             // 2.000.133 DKK - Diverse omkostninger
  };
  const totalFixedCosts = Object.values(fixedCosts).reduce((sum, val) => sum + val, 0);
  
  // Heat company OPEX (Skaelsker Fjernvarme)
  // Variable: Elkøb operational + Sol + Overskudsvarme
  // Fixed: All fixed costs
  const heatOpex = {
    elkøbOperational: operationalElPurchases,
    solOperational: operationalSolarPurchases,
    overskudsvarme: wasteHeatCost,
    forsyningDkLicens: fixedCosts.forsyningDkLicens,
    it: fixedCosts.it,
    revision: fixedCosts.revision,
    administration: fixedCosts.administration,
    driftVedligehold: fixedCosts.driftVedligehold,
    diverse: fixedCosts.diverse,
  };
  
  // Electricity company OPEX (Skaelsker Energi)
  // Variable: Only arbitrage electricity purchases
  const electricityOpex = {
    elkøbArbitrage: arbitrageElPurchases,
  };
  
  const heatTotal = Object.values(heatOpex).reduce((sum, val) => sum + val, 0);
  const electricityTotal = Object.values(electricityOpex).reduce((sum, val) => sum + val, 0);
  
  return {
    heat: { breakdown: heatOpex, total: heatTotal },
    electricity: { breakdown: electricityOpex, total: electricityTotal },
    combined: { total: heatTotal + electricityTotal },
  };
};

/**
 * Allocates revenue between heat and electricity companies
 * @param {Object} stats - Simulation statistics
 * @param {Object} opexSplit - OPEX split data
 * @param {Object} depreciationSplit - Depreciation split data
 * @returns {Object} Revenue allocation
 */
export const allocateRevenue = (stats, opexSplit, depreciationSplit) => {
  // Calculate dynamic heat price based on heat company costs (non-profit)
  // Heat price = (Heat OPEX + Heat Depreciation) / netDelivery
  const netDelivery = stats.netDelivery || 0;
  const heatOpexTotal = opexSplit.heat.total;
  const heatDepreciationTotal = depreciationSplit.heat.total * 1000000; // Convert from M DKK to DKK
  const dynamicHeatPrice = netDelivery > 0 ? (heatOpexTotal + heatDepreciationTotal) / netDelivery : 0;

  // Heat sales (heat company) - using dynamic cost-based pricing
  const heatSales = netDelivery * dynamicHeatPrice;
  
  // Electricity sales from turbine (HEAT company - turbine is part of HTES system)
  const turbineElectricitySales = (stats.turbineRevenue || 0);
  
  // Electricity sales from battery (electricity company)
  const batteryElectricitySales = (stats.batteryRevenue || 0);
  
  // Balance market services from battery (electricity company)
  const batteryServices = (stats.batteryServiceRevenue || 0);
  
  // Balance market services from turbine (HEAT company - turbine is part of heat production)
  const turbineServices = (stats.turbineServiceRevenue || 0);
  
  // Systemydelser from heater (mFRR + aFRR) - goes to electricity company
  // This is when the heater is NOT being used for heating
  // Calculate from balance market based on available hours
  const calculateBalanceMarketRevenue = (stats) => {
    // This should come from simulation stats
    // For now, use a calculation based on available service hours
    const totalHours = 8783;
    const heatingHours = stats.fullPurchaseHours || 0 + stats.partialPurchaseHours || 0;
    const serviceHours = Math.max(0, totalHours - heatingHours);
    
    // Use actual balance market revenue from stats if available
    if (stats.totalBalanceMarketRevenue) {
      return stats.totalBalanceMarketRevenue;
    }
    
    // Otherwise calculate: mFRR (67%) + aFRR (33%)
    const mFRRHours = Math.round(serviceHours * 0.67);
    const aFRRHours = Math.round(serviceHours * 0.33);
    const heaterCapacity = 30; // MW
    
    const mFRRRevenue = mFRRHours * heaterCapacity * 100; // 100 DKK/MW/h
    const aFRRRevenue = aFRRHours * heaterCapacity * 150; // 150 DKK/MW/h
    
    return mFRRRevenue + aFRRRevenue;
  };
  
  const heaterSystemServices = calculateBalanceMarketRevenue(stats);
  
  // Capacity payments (split based on asset contribution)
  const capacityPayments = 366 * 2000; // Daily capacity payment
  const capacityHeat = capacityPayments * 0.6; // 60% to heat (larger capacity)
  const capacityElectricity = capacityPayments * 0.4; // 40% to electricity
  
  // Heat company revenue (includes turbine revenue as it's part of HTES system)
  const heatRevenue = {
    varmesalg: heatSales,
    elsalgTurbine: turbineElectricitySales, // Turbine belongs to heat company
    systemydelserTurbine: turbineServices,    // Turbine services to heat company
    kapacitetsbetaling: capacityHeat,
  };
  
  // Electricity company revenue (only heater and battery)
  const electricityRevenue = {
    elsalgBatteri: batteryElectricitySales,
    systemydelserElkedel: heaterSystemServices, // mFRR + aFRR from heater
    systemydelserBatteri: batteryServices,
    kapacitetsbetaling: capacityElectricity,
  };
  
  const heatTotal = Object.values(heatRevenue).reduce((sum, val) => sum + val, 0);
  const electricityTotal = Object.values(electricityRevenue).reduce((sum, val) => sum + val, 0);
  
  return {
    heat: { breakdown: heatRevenue, total: heatTotal },
    electricity: { breakdown: electricityRevenue, total: electricityTotal },
    combined: { total: heatTotal + electricityTotal },
  };
};

/**
 * Allocates depreciation between heat and electricity companies
 * @returns {Object} Depreciation allocation
 */
export const allocateDepreciation = () => {
  // Depreciation values (in million DKK per year) from CEO's spreadsheet
  const depreciationValues = {
    elkedel: { aarlig: 1.2, levetid: 25 },         // Skaelsker Energi
    htes: { aarlig: 1.6, levetid: 25 },            // Skaelsker Fjernvarme
    tilslutning: { aarlig: 0.18, levetid: 50 },    // Skaelsker Energi
    batterier: { aarlig: 0.9, levetid: 10 },       // Skaelsker Energi
    turbine: { aarlig: 0.38, levetid: 15 },        // Skaelsker Fjernvarme
    ltes: { aarlig: 0.26, levetid: 25 },           // Skaelsker Fjernvarme
    ledningsnet: { aarlig: 5.83, levetid: 50 },    // Skaelsker Fjernvarme
    bygninger: { aarlig: 0.063, levetid: 50 },     // Skaelsker Fjernvarme (3.15M / 50 år)
    orc: { aarlig: 0.04, levetid: 15 },            // Skaelsker Fjernvarme (0.6M / 15 år)
    tilkoblinger: { aarlig: 0.59, levetid: 50 },   // Skaelsker Fjernvarme (29.5M / 50 år)
    industrigrund: { aarlig: 0.08, levetid: 50 },  // Skaelsker Fjernvarme (4M / 50 år)
    veksler: { aarlig: 0.2, levetid: 25 },         // Skaelsker Fjernvarme (5M / 25 år)
    software: { aarlig: 0.21, levetid: 7 },        // Skaelsker Fjernvarme (1.5M / 7 år)
    // Projektledelse og Diverse er engangsomkostninger uden afskrivning
  };

  // Allocate to heat company (Skaelsker Fjernvarme)
  const heatDepreciation = {
    htes: depreciationValues.htes.aarlig,
    turbine: depreciationValues.turbine.aarlig,
    ltes: depreciationValues.ltes.aarlig,
    ledningsnet: depreciationValues.ledningsnet.aarlig,
    bygninger: depreciationValues.bygninger.aarlig,
    orc: depreciationValues.orc.aarlig,
    tilkoblinger: depreciationValues.tilkoblinger.aarlig,
    industrigrund: depreciationValues.industrigrund.aarlig,
    veksler: depreciationValues.veksler.aarlig,
    software: depreciationValues.software.aarlig,
  };

  // Allocate to electricity company (Skaelsker Energi)
  const electricityDepreciation = {
    elkedel: depreciationValues.elkedel.aarlig,
    tilslutning: depreciationValues.tilslutning.aarlig,
    batterier: depreciationValues.batterier.aarlig,
  };
  
  const heatTotal = Object.values(heatDepreciation).reduce((sum, val) => sum + val, 0); // 8.25 M DKK
  const electricityTotal = Object.values(electricityDepreciation).reduce((sum, val) => sum + val, 0); // 2.1 M DKK
  
  return {
    heat: { breakdown: heatDepreciation, total: heatTotal },
    electricity: { breakdown: electricityDepreciation, total: electricityTotal },
    combined: { total: heatTotal + electricityTotal }, // 10.35 M DKK
  };
};

/**
 * Returns an empty financial split object
 * @returns {Object} Empty FinancialSplit
 */
const getEmptyFinancialSplit = () => ({
  heat: {
    capex: {},
    opex: {},
    revenue: {},
    depreciation: {},
    totalCapex: 0,
    totalOpex: 0,
    totalRevenue: 0,
    totalDepreciation: 0,
    ebitda: 0,
    netProfit: 0,
  },
  electricity: {
    capex: {},
    opex: {},
    revenue: {},
    depreciation: {},
    totalCapex: 0,
    totalOpex: 0,
    totalRevenue: 0,
    totalDepreciation: 0,
    ebitda: 0,
    netProfit: 0,
  },
  combined: {
    totalCapex: 0,
    totalOpex: 0,
    totalRevenue: 0,
    totalDepreciation: 0,
    ebitda: 0,
    netProfit: 0,
  },
});

export default {
  splitFinancialData,
  allocateCapex,
  allocateOpex,
  allocateRevenue,
  allocateDepreciation,
};
